import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchUserAttributes, updatePassword, updateUserAttribute, confirmUserAttribute, deleteUser as amplifyDeleteUser } from 'aws-amplify/auth';
import { BookmarkIcon, TagIcon, FolderIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { bookmarkService } from '../services/bookmarkService';

// Allowed avatar colours — must match backend whitelist
const AVATAR_COLORS = [
  { hex: '#4f46e5', label: 'Indigo' },
  { hex: '#7c3aed', label: 'Violet' },
  { hex: '#0284c7', label: 'Sky' },
  { hex: '#0d9488', label: 'Teal' },
  { hex: '#16a34a', label: 'Green' },
  { hex: '#d97706', label: 'Amber' },
  { hex: '#e11d48', label: 'Rose' },
  { hex: '#475569', label: 'Slate' },
];

const DAY_LABELS = { MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday' };

// Derive initials from display name or email
const getInitials = (displayName, email) => {
  const name = (displayName || '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }
  return (email || '?')[0].toUpperCase();
};

// ── Reusable section card ──────────────────────────────────────────────────
const Section = ({ title, children, danger = false }) => (
  <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 ${danger ? 'border-red-200 dark:border-red-800' : 'border-gray-200 dark:border-gray-700'}`}>
    <h2 className={`text-lg font-medium mb-4 ${danger ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>{title}</h2>
    {children}
  </section>
);

const Profile = ({ userEmail: initialEmail }) => {
  const navigate = useNavigate();

  // Account info
  const [email, setEmail] = useState(initialEmail || '');
  const [emailVerified, setEmailVerified] = useState(false);
  const [loadingAttrs, setLoadingAttrs] = useState(true);

  // Change password
  const [pwFields, setPwFields] = useState({ current: '', next: '', confirm: '' });
  const [pwStatus, setPwStatus] = useState({ saving: false, error: '', success: '' });

  // Change email (two-step)
  const [emailStep, setEmailStep] = useState('email');
  const [newEmail, setNewEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [emailStatus, setEmailStatus] = useState({ saving: false, error: '', success: '' });

  // Stats
  const [stats, setStats] = useState(null);
  const [topTags, setTopTags] = useState([]);

  // Preferences (profile identity + digest settings)
  const [prefs, setPrefs] = useState(null);
  const [prefsDirty, setPrefsDirty] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsStatus, setPrefsStatus] = useState({ error: '', success: '' });
  const [copied, setCopied] = useState(false);

  // Delete account
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchUserAttributes()
      .then(attrs => {
        setEmail(attrs.email || '');
        setEmailVerified(attrs.email_verified === true || attrs.email_verified === 'true');
      })
      .catch(() => {})
      .finally(() => setLoadingAttrs(false));

    Promise.all([
      bookmarkService.getUserStats().catch(() => ({})),
      bookmarkService.listCollections().catch(() => ({ items: [] })),
    ]).then(([userStats, cols]) => {
      setStats({ ...userStats, collections: cols.items?.length ?? 0 });
    });
    bookmarkService.getTags().then(({ items: tags }) => {
      const sorted = [...tags].sort((a, b) => (b.count || 0) - (a.count || 0));
      setTopTags(sorted.slice(0, 8));
    }).catch(() => {});
    bookmarkService.getUserPreferences().then(setPrefs).catch(() => {});
  }, []);

  // ── Preferences helpers ────────────────────────────────────────────────────
  const updatePref = useCallback((key, value) => {
    setPrefs(p => ({ ...p, [key]: value }));
    setPrefsDirty(true);
    setPrefsStatus({ error: '', success: '' });
  }, []);

  const handleSavePrefs = async (e) => {
    e.preventDefault();
    setPrefsSaving(true);
    setPrefsStatus({ error: '', success: '' });
    try {
      const updated = await bookmarkService.updateUserPreferences(prefs);
      setPrefs(updated);
      setPrefsDirty(false);
      setPrefsStatus({ error: '', success: 'Saved.' });
      setTimeout(() => setPrefsStatus(s => ({ ...s, success: '' })), 3000);
    } catch (err) {
      setPrefsStatus({ error: err.message || 'Failed to save.', success: '' });
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleCopyProfileLink = () => {
    const url = `${window.location.origin}/u/${prefs.profileShareToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Change Password ────────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwFields.next !== pwFields.confirm) {
      setPwStatus({ saving: false, error: 'New passwords do not match.', success: '' });
      return;
    }
    setPwStatus({ saving: true, error: '', success: '' });
    try {
      await updatePassword({ oldPassword: pwFields.current, newPassword: pwFields.next });
      setPwFields({ current: '', next: '', confirm: '' });
      setPwStatus({ saving: false, error: '', success: 'Password updated.' });
    } catch (err) {
      setPwStatus({ saving: false, error: err.message || 'Failed to update password.', success: '' });
    }
  };

  // ── Change Email ───────────────────────────────────────────────────────────
  const handleSendEmailCode = async (e) => {
    e.preventDefault();
    setEmailStatus({ saving: true, error: '', success: '' });
    try {
      await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: newEmail } });
      setEmailStep('verify');
      setEmailStatus({ saving: false, error: '', success: '' });
    } catch (err) {
      setEmailStatus({ saving: false, error: err.message || 'Failed to send code.', success: '' });
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setEmailStatus({ saving: true, error: '', success: '' });
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: verifyCode });
      const attrs = await fetchUserAttributes();
      setEmail(attrs.email || '');
      setEmailVerified(attrs.email_verified === true || attrs.email_verified === 'true');
      setNewEmail(''); setVerifyCode(''); setEmailStep('email');
      setEmailStatus({ saving: false, error: '', success: 'Email updated.' });
    } catch (err) {
      setEmailStatus({ saving: false, error: err.message || 'Invalid code.', success: '' });
    }
  };

  // ── Delete Account ─────────────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    setDeleting(true); setDeleteError('');
    try {
      await bookmarkService.deleteUser();
      await amplifyDeleteUser();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account.');
      setDeleting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const initials = getInitials(prefs?.displayName, email);
  const avatarColor = prefs?.avatarColor || '#4f46e5';
  const profileUrl = prefs?.profileShareToken ? `${window.location.origin}/u/${prefs.profileShareToken}` : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Profile</h1>

      {/* ── Profile identity card — full width ───────────────────────────── */}
      {prefs !== null && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div
              className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold select-none"
              style={{ backgroundColor: avatarColor }}
            >
              {initials}
            </div>

            {/* Identity fields */}
            <form onSubmit={handleSavePrefs} className="flex-1 min-w-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display name</label>
                  <input
                    type="text"
                    value={prefs.displayName}
                    onChange={e => updatePref('displayName', e.target.value)}
                    maxLength={50}
                    placeholder="Your name"
                    className="form-input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Avatar colour</label>
                  <div className="flex gap-2 flex-wrap">
                    {AVATAR_COLORS.map(({ hex, label }) => (
                      <button
                        key={hex}
                        type="button"
                        title={label}
                        onClick={() => updatePref('avatarColor', hex)}
                        className={`w-7 h-7 rounded-full transition-transform ${prefs.avatarColor === hex ? 'scale-125 ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500' : 'hover:scale-110'}`}
                        style={{ backgroundColor: hex }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bio</label>
                <textarea
                  value={prefs.bio}
                  onChange={e => updatePref('bio', e.target.value)}
                  maxLength={200}
                  rows={2}
                  placeholder="A short description about yourself..."
                  className="form-input text-sm resize-none"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 text-right mt-0.5">{(prefs.bio || '').length}/200</p>
              </div>

              {/* Public profile toggle */}
              <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Public profile</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Share a page showing your public collections</p>
                </div>
                <button
                  type="button"
                  onClick={() => updatePref('profilePublic', !prefs.profilePublic)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                    prefs.profilePublic ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                  role="switch"
                  aria-checked={prefs.profilePublic}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    prefs.profilePublic ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Profile link — shown once public and token exists */}
              {prefs.profilePublic && profileUrl && (
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm">
                  <a
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-primary-600 dark:text-primary-400 hover:underline truncate font-mono text-xs"
                  >
                    {profileUrl}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyProfileLink}
                    className="flex-shrink-0 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    {copied ? <CheckIcon className="h-4 w-4 text-green-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
              {prefs.profilePublic && !profileUrl && (
                <p className="text-xs text-gray-400 dark:text-gray-500">Save to generate your profile link.</p>
              )}

              {prefsStatus.error && <p className="text-sm text-red-600 dark:text-red-400">{prefsStatus.error}</p>}
              {prefsStatus.success && <p className="text-sm text-green-600 dark:text-green-400">{prefsStatus.success}</p>}

              {prefsDirty && (
                <div className="flex justify-end">
                  <button type="submit" disabled={prefsSaving} className="btn-primary">
                    {prefsSaving ? 'Saving...' : 'Save profile'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </section>
      )}

      {/* ── Stats — full width ────────────────────────────────────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Your Library</h2>
        {stats === null ? (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link to="/bookmarks" className="flex flex-col items-center bg-gray-50 dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg p-5 transition-colors">
              <BookmarkIcon className="h-7 w-7 text-primary-600 mb-1" />
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.bookmarkCount ?? 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Bookmarks</p>
            </Link>
            <Link to="/collections" className="flex flex-col items-center bg-gray-50 dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg p-5 transition-colors">
              <FolderIcon className="h-7 w-7 text-indigo-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.collections ?? 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Collections</p>
            </Link>
            <Link to="/bookmarks" state={{ readLaterOnly: true }} className="flex flex-col items-center bg-gray-50 dark:bg-gray-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg p-5 transition-colors">
              <BookmarkSolidIcon className="h-7 w-7 text-amber-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.readLaterCount ?? 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Reading List</p>
            </Link>
            <Link to="/tags" className="flex flex-col items-center bg-gray-50 dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg p-5 transition-colors">
              <TagIcon className="h-7 w-7 text-green-600 mb-1" />
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.tagCount ?? 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Tags</p>
            </Link>
          </div>
        )}
      </section>

      {/* ── Top tags — full width ─────────────────────────────────────────── */}
      {topTags.length > 0 && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Top Tags</h2>
          <div className="flex flex-wrap gap-2">
            {topTags.map(t => (
              <Link
                key={t.tagName}
                to="/bookmarks"
                state={{ filterTag: t.tagName }}
                className="flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-800 dark:text-primary-300 rounded-full text-sm font-medium transition-colors"
              >
                {t.tagName}
                {t.count > 0 && <span className="text-primary-500 dark:text-primary-400 text-xs">{t.count}</span>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Settings — two explicit columns on md+, single column on mobile ── */}
      {/* Each column is an independent flex stack, so cards within a column  */}
      {/* sit flush against each other with no cross-column row alignment.    */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

        {/* Left column: Account + Change Email */}
        <div className="space-y-6">
          <Section title="Account">
            {loadingAttrs ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Email</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate ml-2">{email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Status</span>
                  {emailVerified
                    ? <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">Verified</span>
                    : <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 rounded-full">Unverified</span>
                  }
                </div>
              </div>
            )}
          </Section>

          <Section title="Change Email">
            {emailStep === 'email' ? (
              <form onSubmit={handleSendEmailCode} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New email address</label>
                  <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} required className="form-input text-sm" />
                </div>
                {emailStatus.error && <p className="text-sm text-red-600 dark:text-red-400">{emailStatus.error}</p>}
                <div className="flex justify-end pt-1">
                  <button type="submit" disabled={emailStatus.saving} className="btn-primary text-sm">
                    {emailStatus.saving ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyEmail} className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Code sent to <strong>{newEmail}</strong>.</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Verification code</label>
                  <input type="text" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} required className="form-input text-sm" />
                </div>
                {emailStatus.error && <p className="text-sm text-red-600 dark:text-red-400">{emailStatus.error}</p>}
                <div className="flex justify-between pt-1">
                  <button type="button" onClick={() => { setEmailStep('email'); setEmailStatus({ saving: false, error: '', success: '' }); }} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">Back</button>
                  <button type="submit" disabled={emailStatus.saving} className="btn-primary text-sm">{emailStatus.saving ? 'Verifying...' : 'Verify'}</button>
                </div>
              </form>
            )}
          </Section>
        </div>

        {/* Right column: Change Password + Email Notifications */}
        <div className="space-y-6">
          <Section title="Change Password">
            <form onSubmit={handleChangePassword} className="space-y-3">
              {['current', 'next', 'confirm'].map((field, i) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {['Current password', 'New password', 'Confirm new password'][i]}
                  </label>
                  <input
                    type="password"
                    value={pwFields[field]}
                    onChange={e => setPwFields(f => ({ ...f, [field]: e.target.value }))}
                    required
                    className="form-input text-sm"
                  />
                </div>
              ))}
              {pwStatus.error && <p className="text-sm text-red-600 dark:text-red-400">{pwStatus.error}</p>}
              {pwStatus.success && <p className="text-sm text-green-600 dark:text-green-400">{pwStatus.success}</p>}
              <div className="flex justify-end pt-1">
                <button type="submit" disabled={pwStatus.saving} className="btn-primary text-sm">
                  {pwStatus.saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </Section>

          {prefs !== null && (
            <Section title="Email Notifications">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Weekly summary of your library activity.</p>
              <form onSubmit={handleSavePrefs} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Weekly digest</p>
                    {prefs.digestEnabled && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Every {DAY_LABELS[prefs.digestDay]}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => updatePref('digestEnabled', !prefs.digestEnabled)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${prefs.digestEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                    role="switch"
                    aria-checked={prefs.digestEnabled}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${prefs.digestEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
                {prefs.digestEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Send on</label>
                    <select
                      value={prefs.digestDay}
                      onChange={e => updatePref('digestDay', e.target.value)}
                      className="form-input text-sm w-full"
                    >
                      {Object.entries(DAY_LABELS).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {prefsDirty && (
                  <div className="flex justify-end">
                    <button type="submit" disabled={prefsSaving} className="btn-primary text-sm">
                      {prefsSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </form>
            </Section>
          )}
        </div>

      </div>

      {/* ── Delete account — full width at bottom ─────────────────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-200 dark:border-red-800 p-6">
        <h2 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">Delete Account</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Permanently deletes all bookmarks, tags, and data. Cannot be undone.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type <strong>DELETE</strong> to confirm</label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="form-input text-sm"
            />
            {deleteError && <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>}
          </div>
          <div className="flex sm:justify-end">
            <button
              onClick={handleDeleteAccount}
              disabled={deleteConfirm !== 'DELETE' || deleting}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleting ? 'Deleting...' : 'Delete My Account'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Profile;
